import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Modal from 'react-modal';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { GoogleMap, LoadScript, DirectionsService, DirectionsRenderer, Marker } from '@react-google-maps/api';
import {
  AUTO_DEACTIVATE_SETTINGS_EVENT,
  loadAutoDeactivateSettings
} from '../utils/autoDeactivateSettings';

const DashboardContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1001;

  span {
    display: block;
    width: 25px;
    height: 3px;
    background-color: #333;
    transition: all 0.3s;
    
    &:nth-child(1) {
      transform: ${props => props.isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.isOpen ? '0' : '1'};
    }
    
    &:nth-child(3) {
      transform: ${props => props.isOpen ? 'rotate(-45deg) translate(7px, -7px)' : 'none'};
    }
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const Menu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 250px;
  height: 100%;
  background-color: white;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  padding: 80px 20px 20px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 1rem;
  animation: ${slideIn} 0.3s ease-out;
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: ${props => {
    if (props.variant === 'signout') return '#dc3545';
    if (props.variant === 'deactivate') return '#28a745';
    return '#4285f4';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  ${props => props.disabled && `
    opacity: 0.7;
    cursor: not-allowed;
  `}

  &:hover {
    background-color: ${props => {
      if (props.variant === 'signout') return '#c82333';
      if (props.variant === 'deactivate') return '#218838';
      return '#357abd';
    }};
  }
`;

const AlarmCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const AlarmCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  h2 {
    margin: 0;
  }
`;

const AlarmActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.danger ? '#dc3545' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;

  &:hover {
    background: ${props => props.danger ? '#c82333' : '#5a6268'};
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  margin: 1rem 0;
  border-radius: 8px;
  overflow: hidden;
`;

const NoAlarms = styled.div`
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ETAInfo = styled.div`
  background: #e9ecef;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-weight: bold;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e9ecef;
  border-radius: 2px;
  margin: 1rem 0;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: 40%;
    height: 100%;
    background-color: #4285f4;
    animation: loading 1s infinite ease-in-out;
    border-radius: 2px;
  }

  @keyframes loading {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(250%); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #f8d7da;
  padding: 0.75rem;
  border-radius: 4px;
  margin: 1rem 0;
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #28a745;
  background-color: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  text-align: center;
  font-size: 1.1rem;
  min-width: 300px;
  animation: ${fadeOut} 2s ease-out forwards;
  animation-delay: 1s;
`;

const mapOptions = {
  draggable: true,
  zoomControl: true,
  scrollwheel: true,
  disableDoubleClickZoom: false,
  streetViewControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
  gestureHandling: 'greedy'
};

const getApiUrl = () => {
  return window.RUNTIME_CONFIG?.apiUrl || process.env.REACT_APP_API_URL;
};

const isAlarmWithinAutoDeactivateWindow = (alarm, windowMinutes) => {
  const alarmTime = new Date(alarm.time).getTime();

  if (Number.isNaN(alarmTime)) {
    return false;
  }

  return Math.abs(alarmTime - Date.now()) <= windowMinutes * 60 * 1000;
};

const isAlarmEditable = (alarm, currentTime) => {
  const createdAt = new Date(alarm.createdAt).getTime();

  if (!alarm.active || Number.isNaN(createdAt)) {
    return false;
  }

  return currentTime - createdAt <= 3 * 60 * 1000;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [directions, setDirections] = useState({});
  const [etaTimes, setEtaTimes] = useState({});
  const [deactivating, setDeactivating] = useState({});
  const [deactivateErrors, setDeactivateErrors] = useState({});
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [googleLoadError, setGoogleLoadError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [autoDeactivateSettings, setAutoDeactivateSettings] = useState(loadAutoDeactivateSettings);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const autoDeactivateInFlightRef = useRef(new Set());
  const activeAlarms = useMemo(() => alarms.filter(alarm => alarm.active), [alarms]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let locationWatcher = null;
    
    // Set up continuous location monitoring
    if (navigator.geolocation) {
      locationWatcher = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 15000
        }
      );
    }

    // Fetch initial data
    const fetchData = async () => {
      try {
        const alarmsResponse = await axios.get(`${getApiUrl()}/alarms`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        });
        setAlarms(alarmsResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup function to remove the location watcher
    return () => {
      if (locationWatcher !== null) {
        navigator.geolocation.clearWatch(locationWatcher);
      }
    };
  }, [user]); // Include user in dependencies since we use it in fetchData

  useEffect(() => {
    const reloadSettings = () => {
      setAutoDeactivateSettings(loadAutoDeactivateSettings());
    };

    window.addEventListener('storage', reloadSettings);
    window.addEventListener(AUTO_DEACTIVATE_SETTINGS_EVENT, reloadSettings);

    return () => {
      window.removeEventListener('storage', reloadSettings);
      window.removeEventListener(AUTO_DEACTIVATE_SETTINGS_EVENT, reloadSettings);
    };
  }, []);

  const handleDeactivate = useCallback(async (alarmId, options = {}) => {
    const { automatic = false } = options;

    if (!userLocation) {
      if (!automatic) {
        setDeactivateErrors(prev => ({
          ...prev,
          [alarmId]: 'Unable to get your current location. Please enable location services.'
        }));
      }
      return;
    }

    if (autoDeactivateInFlightRef.current.has(alarmId)) {
      return;
    }

    autoDeactivateInFlightRef.current.add(alarmId);

    if (!automatic) {
      setDeactivating(prev => ({ ...prev, [alarmId]: true }));
      setDeactivateErrors(prev => ({ ...prev, [alarmId]: null }));
    }

    try {
      const response = await axios.post(
        `${getApiUrl()}/alarms/deactivate`,
        {
          alarmId,
          currentLocation: userLocation
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      if (response.data.success) {
        // Show success message
        setSuccessMessage('Alarm successfully deactivated');
        
        // Remove the deactivated alarm from the state entirely
        setAlarms(prev => prev.filter(alarm => alarm.id !== alarmId));
        
        // Clean up related state
        setDirections(prev => {
          const newDirections = { ...prev };
          delete newDirections[alarmId];
          return newDirections;
        });
        setEtaTimes(prev => {
          const newEtaTimes = { ...prev };
          delete newEtaTimes[alarmId];
          return newEtaTimes;
        });

        // Clear success message after animation
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else if (!automatic) {
        setDeactivateErrors(prev => ({
          ...prev,
          [alarmId]: response.data.message
        }));
      }
    } catch (error) {
      if (!automatic) {
        setDeactivateErrors(prev => ({
          ...prev,
          [alarmId]: error.response?.data?.message || 'Failed to fulfill pledge'
        }));
      }
    } finally {
      autoDeactivateInFlightRef.current.delete(alarmId);

      if (!automatic) {
        setDeactivating(prev => ({ ...prev, [alarmId]: false }));
      }
    }
  }, [user, userLocation]);

  const handleDeleteAlarm = async (alarmId) => {
    if (!window.confirm('Delete this alarm?')) {
      return;
    }

    try {
      await axios.delete(`${getApiUrl()}/alarms/${alarmId}`, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });

      setAlarms(prev => prev.filter(alarm => alarm.id !== alarmId));
      setSuccessMessage('Alarm deleted successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      setDeactivateErrors(prev => ({
        ...prev,
        [alarmId]: error.response?.data?.message || 'Failed to delete alarm'
      }));
    }
  };

  useEffect(() => {
    if (!autoDeactivateSettings.enabled || loading || !userLocation) {
      return undefined;
    }

    const tryAutoDeactivate = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      activeAlarms
        .filter(alarm => isAlarmWithinAutoDeactivateWindow(alarm, autoDeactivateSettings.windowMinutes))
        .forEach(alarm => {
          handleDeactivate(alarm.id, { automatic: true });
        });
    };

    tryAutoDeactivate();
    const intervalId = window.setInterval(
      tryAutoDeactivate,
      autoDeactivateSettings.intervalSeconds * 1000
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    activeAlarms,
    autoDeactivateSettings.enabled,
    autoDeactivateSettings.intervalSeconds,
    autoDeactivateSettings.windowMinutes,
    handleDeactivate,
    loading,
    userLocation
  ]);

  const directionsCallback = useCallback((alarmId) => (result, status) => {
    if (status === 'OK' && !directions[alarmId]) {
      setDirections(prev => ({
        ...prev,
        [alarmId]: result
      }));
      
      const duration = result.routes[0].legs[0].duration.text;
      setEtaTimes(prev => ({
        ...prev,
        [alarmId]: duration
      }));
    }
  }, [directions]);

  const formatTime = (time) => {
    return new Date(time).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleGoogleLoad = () => {
    setIsGoogleLoaded(true);
    setGoogleLoadError(null);
  };

  const handleGoogleError = (error) => {
    console.error('Google Maps failed to load:', error);
    setGoogleLoadError('Failed to load Google Maps. Please try refreshing the page.');
    setIsGoogleLoaded(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <LoadScript 
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      version="quarterly"
      onLoad={handleGoogleLoad}
      onError={handleGoogleError}
    >
      <DashboardContainer>
        <Header>
          <Title>Your Alarms</Title>
          <HamburgerButton onClick={() => setIsMenuOpen(!isMenuOpen)} isOpen={isMenuOpen}>
            <span />
            <span />
            <span />
          </HamburgerButton>
        </Header>

        <MenuOverlay isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
        <Menu isOpen={isMenuOpen}>
          {user.isAdmin && (
            <Button onClick={async () => {
              try {
                const response = await axios.get(`${getApiUrl()}/password`, {
                  headers: { Authorization: `Bearer ${await user.getIdToken()}` }
                });
                setModalMessage(response.data.password);
                setIsModalOpen(true);
              } catch (error) {
                setModalMessage(error.response?.data?.message || 'Failed to retrieve password');
                setIsModalOpen(true);
              }
              setIsMenuOpen(false);
            }}>
              Get Password
            </Button>
          )}
          <Button onClick={() => {
            navigate('/create-alarm');
            setIsMenuOpen(false);
          }}>
            Create New Alarm
          </Button>
          <Button onClick={() => {
            navigate('/visualization');
            setIsMenuOpen(false);
          }}>
            View Alarm Stats
          </Button>
          <Button onClick={() => {
            navigate('/settings');
            setIsMenuOpen(false);
          }}>
            Settings
          </Button>
          <Button variant="signout" onClick={() => {
            handleSignOut();
            setIsMenuOpen(false);
          }}>
            Sign Out
          </Button>
        </Menu>

        {successMessage && (
          <SuccessMessage>{successMessage}</SuccessMessage>
        )}

        {googleLoadError && (
          <ErrorMessage>{googleLoadError}</ErrorMessage>
        )}

        {activeAlarms.length > 0 ? (
          activeAlarms.map((alarm) => (
            <AlarmCard key={alarm.id}>
              <AlarmCardHeader>
                <h2>{formatTime(alarm.time)}</h2>
                {isAlarmEditable(alarm, currentTime) && (
                  <AlarmActions>
                    <IconButton
                      type="button"
                      title="Edit alarm"
                      aria-label="Edit alarm"
                      onClick={() => navigate(`/alarms/${alarm.id}/edit`)}
                    >
                      ✎
                    </IconButton>
                    <IconButton
                      type="button"
                      danger
                      title="Delete alarm"
                      aria-label="Delete alarm"
                      onClick={() => handleDeleteAlarm(alarm.id)}
                    >
                      ×
                    </IconButton>
                  </AlarmActions>
                )}
              </AlarmCardHeader>
              {alarm.location && userLocation && isGoogleLoaded && (
                <>
                  <MapContainer>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={alarm.location}
                      zoom={12}
                      options={mapOptions}
                    >
                      <DirectionsService
                        options={{
                          destination: alarm.location,
                          origin: userLocation,
                          travelMode: 'DRIVING'
                        }}
                        callback={directionsCallback(alarm.id)}
                      />
                      
                      {directions[alarm.id] && (
                        <DirectionsRenderer
                          options={{
                            directions: directions[alarm.id],
                            suppressMarkers: true,
                            draggable: false
                          }}
                        />
                      )}

                      {window.google && (
                        <>
                          <Marker
                            position={userLocation}
                            icon={{
                              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                              labelOrigin: new window.google.maps.Point(0, -32)
                            }}
                            label={{
                              text: "Your Location",
                              color: "#4285F4",
                              fontWeight: "bold",
                              fontSize: "14px",
                              className: "marker-label"
                            }}
                          />

                          <Marker
                            position={alarm.location}
                            icon={{
                              url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                              labelOrigin: new window.google.maps.Point(0, -32)
                            }}
                            label={{
                              text: "Alarm",
                              color: "#DB4437",
                              fontWeight: "bold",
                              fontSize: "14px",
                              className: "marker-label"
                            }}
                          />
                        </>
                      )}
                    </GoogleMap>
                  </MapContainer>
                  {etaTimes[alarm.id] && (
                    <ETAInfo>
                      Estimated driving time: {etaTimes[alarm.id]}
                    </ETAInfo>
                  )}
                </>
              )}
              <p>Punishment Amount: {formatMoney(alarm.amount)}</p>
              <Button
                variant="deactivate"
                onClick={() => handleDeactivate(alarm.id)}
                disabled={deactivating[alarm.id]}
              >
                {deactivating[alarm.id] ? 'Deactivating...' : 'Deactivate Alarm'}
              </Button>
              
              {deactivating[alarm.id] && <ProgressBar />}
              
              {deactivateErrors[alarm.id] && (
                <ErrorMessage>{deactivateErrors[alarm.id]}</ErrorMessage>
              )}
            </AlarmCard>
          ))
        ) : (
          <NoAlarms>
            <h2>No Active Alarms</h2>
            <p>
              <Link to="/create-alarm" style={{ color: '#4285f4', textDecoration: 'underline' }}>
                Create a new alarm
              </Link>
              {' '}to get started
            </p>
          </NoAlarms>
        )}
          <Modal
            isOpen={isModalOpen}
            onRequestClose={() => setIsModalOpen(false)}
            style={{
              content: {
                top: '50%',
                left: '50%',
                right: 'auto',
                bottom: 'auto',
                marginRight: '-50%',
                transform: 'translate(-50%, -50%)',
                padding: '20px',
                borderRadius: '8px',
                maxWidth: '400px',
                width: '90%'
              },
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ marginBottom: '20px' }}>{modalMessage}</h2>
              <Button onClick={() => setIsModalOpen(false)}>Close</Button>
            </div>
          </Modal>
        </DashboardContainer>
    </LoadScript>
  );
};

export default Dashboard;
